import { Box, Text } from '@user-portal/wikr-components';
import { Trans } from 'react-i18next';
import { useEffect, useState } from 'react';

import BrowserStorage from 'api/utils/browserStorage';

import { selectCurrentUser } from 'store/user/selectors';
import { selectCancellationCandidates } from 'store/subscriptions/selectors';
import { selectDeepLink } from 'store/deepLink/selectors';
import { getDeepLinkRequest } from 'store/deepLink/actions';
import { sendRetenoAnalyticsRequest } from 'store/analytics/actions';

import { YG_CABINET_DEEPLINK_CLICK } from 'constants/dataDog';
import { Platform, PLATFORM_BY_KEY } from 'constants/api';

import { trackDatadogRumAction } from 'services/DataDog';
import { trackScreenLoad } from 'services/Analytics/trackers/mainTrackers';
import {
    sendAnalyticCancelOfferClick,
    sendAnalyticDeeplinkLoadClick,
    sendAnalyticDownloadAppClick,
    trackScreenLoadCancelOffer,
} from 'services/Analytics';

import { useDispatch, useSelector } from 'hooks/store';

import { getValuesByKey } from 'helpers/utils';
import { LS_KEYS } from 'helpers/localStorage';
import isMobile from 'helpers/isMobile';

import StoreButton from './components/StoreButton';
import PlanInfo from './components/PlanInfo';
import CopyCodeStep from './components/CopyCodeStep';

import * as S from './styled';

import { CancelOfferAction } from 'types/subscription';
import { IInstallAppSteps } from './types';

const InstallAppSteps = ({ customLink, cancelOfferName = null }: IInstallAppSteps) => {
    const dispatch = useDispatch();

    const deepLinkFromServer = useSelector(selectDeepLink);
    const { id, email, first_name } = useSelector(selectCurrentUser);

    const deepLinkUrl = customLink || deepLinkFromServer;

    const isAndroid = isMobile.android();
    const isIos = isMobile.iOS();
    const isMobileDevice = isAndroid || isIos;

    const cancellationCandidates = useSelector(selectCancellationCandidates);
    const isCancelOffer = Boolean(cancelOfferName);

    const subscription_id = getValuesByKey(cancellationCandidates, 'external_id');

    const [isUserFirstTimeOnMainPageAfterOB, setIsUserFirstTimeOnMainPageAfterOB] = useState<boolean>(false);

    useEffect(() => {
        const isFirstVisitAfterOB = Boolean(localStorage.getItem(LS_KEYS.isUserFirstTimeOnMainPageAfterOB));

        setIsUserFirstTimeOnMainPageAfterOB(isFirstVisitAfterOB);

        return () => {
            isFirstVisitAfterOB && localStorage.removeItem(LS_KEYS.isUserFirstTimeOnMainPageAfterOB);
        };
    }, []);

    useEffect(() => {
        if (!deepLinkUrl) dispatch(getDeepLinkRequest());
    }, [deepLinkUrl, dispatch]);

    useEffect(() => {
        isCancelOffer
            ? trackScreenLoadCancelOffer({ eventLabel: { subscription_id, offer: cancelOfferName } })
            : trackScreenLoad();

        isUserFirstTimeOnMainPageAfterOB &&
            !isCancelOffer &&
            dispatch(
                sendRetenoAnalyticsRequest({
                    event_type_key: 'main__screen__load',
                    event_params: {
                        externalCustomerId: id,
                        email,
                        time: new Date().toISOString(),
                        platform: PLATFORM_BY_KEY[Platform.Web],
                    },
                })
            );
    }, [isCancelOffer, dispatch, subscription_id, cancelOfferName, id, email, isUserFirstTimeOnMainPageAfterOB]);

    useEffect(() => {
        deepLinkUrl && sendAnalyticDeeplinkLoadClick();
    }, [deepLinkUrl]);

    const handleDownloadApp = (eventLabel: string, isDeeplinkBtn = true) => {
        BrowserStorage.setItem('isAppDownloaded', true);

        !isDeeplinkBtn && window.open(deepLinkUrl, '_blank', 'noopener,noreferrer');

        if (isMobile.any()) {
            trackDatadogRumAction(YG_CABINET_DEEPLINK_CLICK, { eventLabel });
        }

        isCancelOffer
            ? sendAnalyticCancelOfferClick({
                  eventLabel: { subscription_id, offer: cancelOfferName },
                  actionEvent: CancelOfferAction.Accept,
              })
            : sendAnalyticDownloadAppClick(eventLabel);

        isUserFirstTimeOnMainPageAfterOB &&
            !isCancelOffer &&
            dispatch(
                sendRetenoAnalyticsRequest({
                    event_type_key: 'main__click',
                    event_params: {
                        event_label: eventLabel,
                        externalCustomerId: id,
                        email,
                        time: new Date().toISOString(),
                        platform: PLATFORM_BY_KEY[Platform.Web],
                    },
                })
            );
    };
    const descriptionText = `downloadApp.simplifiedVersion.${
        isMobileDevice ? 'mobile' : 'desktop'
    }.description--dynamic-key`;

    return (
        <S.Container>
            <PlanInfo isMobileDevice={isMobileDevice} deepLinkUrl={deepLinkUrl} />

            <Box>
                <S.Title type="h3" mb={12} medium isMobileDevice={isMobileDevice}>
                    <Trans
                        i18nKey="downloadApp.simplifiedVersion.title"
                        components={{ 1: <S.TitleHighlight /> }}
                        values={{
                            name: first_name,
                            hasName: first_name ? 'yes' : 'no',
                        }}
                    />
                </S.Title>
                <Text mb={24} type="large-text" color="text-secondary">
                    <Trans i18nKey={descriptionText} components={{ 1: <S.HighlightEmail /> }} values={{ email }} />
                </Text>

                <CopyCodeStep isMobileDevice={Boolean(isMobileDevice)} />

                {isMobileDevice && <StoreButton isIos={isIos} handleLinkDownloadApp={handleDownloadApp} />}
            </Box>
        </S.Container>
    );
};

export default InstallAppSteps;
